/*
Anonymize actions / aggregates so that users in one track can't see the names
and (optionally) notes / feedback of users in another track.
*/
import { create } from "mutative";

import { isRichTextBlank } from "@lib/isRichTextBlank";
import type { IncomingActionMessage } from "@engine/core";
import {
  assertGetMember,
  getHiddenRecommenderMemberIds,
  isRestrictedFunderViewer,
} from "@engine/round/accessors";
import type { NotesAndFeedbackVisibility } from "@engine/round/actions";
import type { RoundAggregate } from "@engine/round/aggregate";
import type { UserProfile } from "@lib/types";
import { type Track, trackNames, trackSchema } from "@lib/types";

type MinimalRoundMember = {
  userId: string;
  createdAt: string;
  track: string | Track;
  feedbackVisibleByDefault: boolean;
  evaluablePermissions: {
    [evaluableId: string]: NotesAndFeedbackVisibility;
  };
  deanonymize: boolean;
};

export type TrackMembers = {
  byTrack: Record<Track, MinimalRoundMember[]>;
  byUserId: Record<string, MinimalRoundMember>;
};

const trackInitials: Record<Track, string> = {
  MAIN: "M",
  FAIRNESS: "FA",
  FREEDOM: "FR",
  NO_TRACK: "NT",
};

export const redacted = "REDACTED";
const EMPTY_HIDDEN_RECOMMENDER_MEMBER_IDS: ReadonlySet<string> = new Set();

export function makeTrackMembers(round: RoundAggregate): TrackMembers {
  const roundMembers = Object.values(round.members);

  const trackMembers: TrackMembers = {
    byTrack: {
      MAIN: [],
      FAIRNESS: [],
      FREEDOM: [],
      NO_TRACK: [],
    },
    byUserId: {},
  };

  roundMembers.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  for (const member of roundMembers) {
    const track = trackSchema.parse(member.track);
    trackMembers.byTrack[track].push(member);
    trackMembers.byUserId[member.userId] = member;
  }

  return trackMembers;
}

export function canViewBasedOnTrack(viewerTrack: Track, track: Track): boolean {
  return (
    viewerTrack === "NO_TRACK" || track === "NO_TRACK" || viewerTrack === track
  );
}

export function canViewActionDeanonymized(
  viewerTrack: Track,
  viewerUserId: string | undefined,
  round: RoundAggregate,
  msg: IncomingActionMessage,
): boolean {
  const hiddenRecommenderMemberIds =
    viewerUserId == null
      ? EMPTY_HIDDEN_RECOMMENDER_MEMBER_IDS
      : getHiddenRecommenderMemberIds(round, viewerUserId);
  return canViewActionDeanonymizedWithRestrictions(
    viewerTrack,
    hiddenRecommenderMemberIds,
    round,
    msg,
  );
}

function canViewActionDeanonymizedWithRestrictions(
  viewerTrack: Track,
  hiddenRecommenderMemberIds: ReadonlySet<string>,
  round: RoundAggregate,
  msg: IncomingActionMessage,
): boolean {
  const action = msg.payload;

  switch (action.type) {
    case "CREATE_ROUND":

    case "ADD_ROUND_MEMBER": {
      const member = assertGetMember(round, action.userId);
      return (
        member.deanonymize || canViewBasedOnTrack(viewerTrack, action.track)
      );
    }

    case "PROPOSAL_DISCLOSURE_CHANGE": {
      if (hiddenRecommenderMemberIds.has(action.userId)) {
        return false;
      }

      const member = assertGetMember(round, action.userId);
      return canViewBasedOnTrack(viewerTrack, member.track);
    }

    case "EVALUATION_FEEDBACK_CHANGE":

    case "EVALUATION_NOTES_CHANGE": {
      if (hiddenRecommenderMemberIds.has(action.memberId)) {
        return false;
      }

      // TODO: does this break if we delete the member?
      const {
        track: memberTrack,
        feedbackVisibleByDefault,
        evaluablePermissions,
      } = assertGetMember(round, action.memberId);
      const permissionsForThisEvaluable =
        evaluablePermissions[action.evaluableId];

      if (canViewBasedOnTrack(viewerTrack, memberTrack)) {
        return true;
      }

      if (permissionsForThisEvaluable === "hidden") {
        return false;
      }

      if (permissionsForThisEvaluable === "visible") {
        return true;
      }

      return feedbackVisibleByDefault;
    }

    case "GENERAL_NOTES_CHANGE": {
      if (hiddenRecommenderMemberIds.has(action.memberId)) {
        return false;
      }

      // No per-evaluable visibility flag for general notes, so fall back to the
      // member's default (like notes/feedback): visible to everyone when
      // feedback is visible by default, otherwise limited to the same track.
      const { track: memberTrack, feedbackVisibleByDefault } = assertGetMember(
        round,
        action.memberId,
      );
      return (
        canViewBasedOnTrack(viewerTrack, memberTrack) ||
        feedbackVisibleByDefault
      );
    }

    case "ADD_EVALUATOR": {
      const member = assertGetMember(round, action.memberId);
      return (
        member.deanonymize || canViewBasedOnTrack(viewerTrack, member.track)
      );
    }

    case "ADMIN_CHANGE_ROUND_MEMBER_PROPERTY": {
      const member = assertGetMember(round, action.memberId);
      return (
        member.deanonymize || canViewBasedOnTrack(viewerTrack, member.track)
      );
    }

    case "FUNDER_RATING_CHANGE":
    case "FUNDER_RATINGS_IMPORT":
    case "FUNDER_RATINGS_CLEAR":

    case "CLEAR_FUNDER_RATINGS": {
      return viewerTrack === "NO_TRACK";
    }

    default:
      return true;
  }
}

export function makeActionAnonymizerForViewer(
  viewerTrack: Track,
  viewerUserId: string | undefined,
  round: RoundAggregate,
  trackMembers: TrackMembers = makeTrackMembers(round),
): <A extends IncomingActionMessage>(actionMessage: A) => A {
  const hiddenRecommenderMemberIds =
    viewerUserId == null
      ? EMPTY_HIDDEN_RECOMMENDER_MEMBER_IDS
      : getHiddenRecommenderMemberIds(round, viewerUserId);
  return <A extends IncomingActionMessage>(actionMessage: A): A =>
    canViewActionDeanonymizedWithRestrictions(
      viewerTrack,
      hiddenRecommenderMemberIds,
      round,
      actionMessage,
    )
      ? actionMessage
      : anonymizeAction(
          trackMembers,
          actionMessage,
          hiddenRecommenderMemberIds,
        );
}

export function anonymizeTrackMember(
  trackMembers: TrackMembers,
  userId: string,
): {
  shortname: string;
  initials: string;
} | null {
  for (const track of Object.keys(trackMembers.byTrack) as Track[]) {
    const memberIndex = trackMembers.byTrack[track].findIndex(
      (member: MinimalRoundMember) => member.userId === userId,
    );

    if (memberIndex !== -1) {
      return {
        shortname: `${trackNames[track]} member ${memberIndex + 1}`,
        initials: trackInitials[track] + (memberIndex + 1),
      };
    }
  }

  return null;
}

export function anonymizeAction<A extends IncomingActionMessage>(
  trackMembers: TrackMembers,
  actionMessage: A,
  hiddenRecommenderMemberIds: ReadonlySet<string> = EMPTY_HIDDEN_RECOMMENDER_MEMBER_IDS,
): A {
  return create(actionMessage, (draft) => {
    const action = draft.payload;

    switch (action.type) {
      case "ADD_ROUND_MEMBER": {
        const anonymized = anonymizeTrackMember(trackMembers, action.userId);
        action.initials = anonymized == null ? redacted : anonymized.initials;
        return;
      }

      case "CREATE_ROUND": {
        const anonymized = anonymizeTrackMember(trackMembers, action.userId);
        action.initials = anonymized == null ? redacted : anonymized.initials;
        return;
      }

      case "PROPOSAL_DISCLOSURE_CHANGE": {
        action.disclosure = redacted;
        return;
      }

      case "ADD_EVALUATOR": {
        const anonymized = anonymizeTrackMember(trackMembers, action.memberId);
        action.shortname = anonymized == null ? redacted : anonymized.shortname;
        return;
      }

      case "EVALUATION_FEEDBACK_CHANGE": {
        if (hiddenRecommenderMemberIds.has(action.memberId)) {
          action.positive = redacted;
          action.negative = redacted;
          return;
        }

        const member = trackMembers.byUserId[action.memberId];

        if (!member) {
          action.positive = redacted;
          action.negative = redacted;
          return;
        }

        const visible = notesOrFeedbackVisible(member, action.evaluableId);

        if (!visible) {
          action.positive = redacted;
          action.negative = redacted;
        }

        return;
      }

      case "EVALUATION_NOTES_CHANGE": {
        if (hiddenRecommenderMemberIds.has(action.memberId)) {
          action.notes = redacted;
          return;
        }

        const member = trackMembers.byUserId[action.memberId];

        if (!member) {
          action.notes = redacted;
          return;
        }

        const visible = notesOrFeedbackVisible(member, action.evaluableId);

        if (!visible) {
          action.notes = redacted;
        }

        return;
      }

      case "GENERAL_NOTES_CHANGE": {
        // Reached only when cross-track (canViewActionDeanonymized was false).
        action.notes = redacted;
        return;
      }

      case "ADMIN_CHANGE_ROUND_MEMBER_PROPERTY": {
        const anonymized = anonymizeTrackMember(trackMembers, action.memberId);

        if (action.initials) {
          action.initials = anonymized == null ? redacted : anonymized.initials;
        }

        return;
      }

      case "FUNDER_RATING_CHANGE": {
        action.rating = undefined;
        return;
      }

      case "FUNDER_RATINGS_IMPORT": {
        action.scores = [];
        return;
      }
    }
  });
}

export function anonymizeUserProfile(
  trackMembers: TrackMembers,
  userProfile: UserProfile,
): UserProfile {
  return create(userProfile, (draft) => {
    const anonymized = anonymizeTrackMember(trackMembers, userProfile.id);
    draft.name = anonymized == null ? redacted : anonymized.shortname;
    draft.email = redacted;
  });
}

/**
 * Results of `anonymizeAggregate`, held **weakly against the round they describe
 * and keyed by viewer within it**, so repeated calls cost one traversal
 * instead of N.
 *
 * The round is matched by **reference identity** (that's what a WeakMap key is).
 * Sound because the aggregate is immutable: reducers build a new object via
 * mutative and the storage adapters only ever reassign their aggregate fields,
 * never mutate one in place. So any change to the round yields a new reference,
 * and a matching reference guarantees a matching result — including for a caller
 * that reads immediately after a dispatch and must not see a stale round.
 *
 * Returning a shared object is no weaker than the uncached behavior already
 * was: mutative returns the input untouched when a draft goes unmutated, so
 * `anonymizeAggregate` already handed callers an object they must treat as
 * immutable.
 *
 * **Why weak keys rather than a plain LRU.** A round aggregate is megabytes; the
 * committed fixture is 3MB of JSON, more as a live object graph, and the primary
 * funder reviews on an iPad. Any cache that holds rounds strongly outlives their
 * usefulness in two separate ways — a superseded version after every edit or
 * confirmation, and a whole round the user has navigated away from — and each
 * wants its own eviction rule. Keying weakly removes the question: an entry
 * lasts exactly as long as something else still holds the round, so the storage
 * adapter dropping a version or a round drops the cached work with it, and this
 * module needs no notion of obsolescence, no round-id bookkeeping, and no
 * teardown hook to call.
 *
 * A round holds one entry per (viewer track, restricted funder) — see
 * `viewerRedactionKey`, which collapses every unrestricted viewer onto one
 * slot — so in practice that is a handful, and typically one. The cap is a
 * backstop for a round with many restricted funders rather than a working-set
 * limit anything normal reaches; entries are most-recently-used first so that,
 * if it ever binds, it drops the coldest.
 */
const ANONYMIZED_VIEWERS_PER_ROUND = 8;

/** Shared slot for every viewer the round redacts nothing extra for. */
const UNRESTRICTED_VIEWER_KEY = "";

/**
 * The part of `viewerUserId` that actually changes the output.
 *
 * `anonymizeAggregateUncached` consults `viewerUserId` for exactly one thing:
 * the hidden-recommender set, which is empty unless the viewer is a restricted
 * funder. Every other viewer of a given round and track gets a byte-identical
 * result — so keying those per user would store one large duplicate graph per
 * user and, past the cap, miss on every request. A round routinely has more
 * members than the cap (22 active in the committed fixture) and typically no
 * restricted funder at all, so the shared slot is the normal case.
 *
 * **Invariant:** if anything else in `anonymizeAggregateUncached` ever varies
 * with `viewerUserId`, this key must grow to cover it — otherwise two viewers
 * who should see different rounds would share one. That is the price of keying
 * on meaning rather than on the raw argument list, and it is why the two
 * branches below mirror the redaction condition exactly.
 *
 * The flag is checked before `isRestrictedFunderViewer` on purpose: the full
 * predicate calls `isUserFunder`, which walks every active member's evaluators,
 * and this runs on every call including cache hits. `isRestrictedFunder === true`
 * is a necessary condition for it, so failing that check rules out restriction
 * with a single map lookup.
 */
function viewerRedactionKey(
  round: RoundAggregate,
  viewerUserId: string | undefined,
): string {
  if (viewerUserId == null) {
    return UNRESTRICTED_VIEWER_KEY;
  }

  if (round.members[viewerUserId]?.isRestrictedFunder !== true) {
    return UNRESTRICTED_VIEWER_KEY;
  }

  return isRestrictedFunderViewer(round, viewerUserId)
    ? viewerUserId
    : UNRESTRICTED_VIEWER_KEY;
}

type AnonymizedViewerEntry = {
  viewerTrack: Track;
  viewerKey: string;
  result: RoundAggregate;
};

let anonymizedByRound = new WeakMap<RoundAggregate, AnonymizedViewerEntry[]>();

/** Test-only: drop the memo so a test can observe an uncached traversal. */
export function resetAnonymizeAggregateMemoForTests(): void {
  anonymizedByRound = new WeakMap();
}

export function anonymizeAggregate(
  viewerTrack: Track,
  round: RoundAggregate,
  viewerUserId?: string,
): RoundAggregate {
  const viewerKey = viewerRedactionKey(round, viewerUserId);
  const viewers = anonymizedByRound.get(round);
  const hit = viewers?.find(
    (entry) =>
      entry.viewerTrack === viewerTrack && entry.viewerKey === viewerKey,
  );

  if (viewers != null && hit != null) {
    // Re-seat as most-recently-used so a steady rotation of viewers keeps its
    // working set instead of aging out the entry it is about to ask for again.
    const hitIndex = viewers.indexOf(hit);

    if (hitIndex > 0) {
      viewers.splice(hitIndex, 1);
      viewers.unshift(hit);
    }

    return hit.result;
  }

  const result = anonymizeAggregateUncached(viewerTrack, round, viewerUserId);
  const entries = viewers ?? [];
  entries.unshift({ viewerTrack, viewerKey, result });

  if (entries.length > ANONYMIZED_VIEWERS_PER_ROUND) {
    entries.length = ANONYMIZED_VIEWERS_PER_ROUND;
  }

  if (viewers == null) {
    anonymizedByRound.set(round, entries);
  }

  return result;
}

function anonymizeAggregateUncached(
  viewerTrack: Track,
  round: RoundAggregate,
  viewerUserId?: string,
): RoundAggregate {
  const trackMembers = makeTrackMembers(round);
  const hiddenRecommenderMemberIds =
    viewerUserId != null && isRestrictedFunderViewer(round, viewerUserId)
      ? getHiddenRecommenderMemberIds(round, viewerUserId)
      : null;
  return create(round, (draft) => {
    for (const [userId, member] of Object.entries(draft.members)) {
      const memberTrack = trackSchema.parse(member.track);
      const anonymized = anonymizeTrackMember(trackMembers, userId);
      const canSeeBasedOnTrack = canViewBasedOnTrack(viewerTrack, memberTrack);

      if (!member.deanonymize && !canSeeBasedOnTrack) {
        member.initials = anonymized == null ? redacted : anonymized.initials;
      }

      // When redacting cross-track content, leave already-blank fields blank
      // rather than stamping them with "REDACTED". A cleared note/feedback has
      // nothing to hide, and masking its emptiness makes it resurface as a
      // misleading "entered notes but not shared" row in the Notes tab.
      for (const [evaluableId, feedback] of Object.entries(
        member.evaluableFeedback,
      )) {
        if (
          !(canSeeBasedOnTrack || notesOrFeedbackVisible(member, evaluableId))
        ) {
          const { positive, negative } = feedback.positiveNegative;

          if (!isRichTextBlank(positive) || !isRichTextBlank(negative)) {
            feedback.positiveNegative.positive = redacted;
            feedback.positiveNegative.negative = redacted;
          }
        }
      }

      for (const evaluableId of Object.keys(member.evaluableNotes)) {
        if (
          !(
            canSeeBasedOnTrack || notesOrFeedbackVisible(member, evaluableId)
          ) &&
          !isRichTextBlank(member.evaluableNotes[evaluableId])
        ) {
          member.evaluableNotes[evaluableId] = redacted;
        }
      }

      // No per-evaluable flag, so fall back to the member's default (like
      // notes/feedback): keep the note when feedback is visible by default,
      // otherwise redact it cross-track.
      if (
        member.generalNotes != null &&
        !(canSeeBasedOnTrack || member.feedbackVisibleByDefault)
      ) {
        member.generalNotes = redacted;
      }

      for (const evaluator of Object.values(member.evaluators)) {
        if (!member.deanonymize && !canSeeBasedOnTrack) {
          evaluator.shortname =
            anonymized == null ? redacted : anonymized.shortname;
        }

        if (evaluator.type === "funder" && viewerTrack !== "NO_TRACK") {
          evaluator.scores = {};
        }
      }
    }

    for (const memberDisclosures of Object.values(draft.proposalDisclosure)) {
      for (const [memberId, disclosure] of Object.entries(memberDisclosures)) {
        const member = draft.members[memberId];

        if (!member) {
          continue;
        }

        const memberTrack = trackSchema.parse(member.track);

        if (!canViewBasedOnTrack(viewerTrack, memberTrack)) {
          disclosure.disclosure = redacted;
        }
      }
    }

    // Restricted funder view: hidden recommenders' notes,
    // feedback, and general notes are deleted entirely (not stamped
    // "REDACTED") so downstream hasNote checks read as false. Their
    // evaluators (MVFs/scores), initials, and shortnames are left untouched.
    if (hiddenRecommenderMemberIds != null) {
      for (const hiddenMemberId of hiddenRecommenderMemberIds) {
        const member = draft.members[hiddenMemberId];

        if (!member) {
          continue;
        }

        for (const evaluableId of Object.keys(member.evaluableNotes)) {
          delete member.evaluableNotes[evaluableId];
        }

        for (const evaluableId of Object.keys(member.evaluableFeedback)) {
          delete member.evaluableFeedback[evaluableId];
        }

        delete member.generalNotes;
      }

      // Proposal disclosures: blank only the hidden recommender's
      // authored free-text (the "disclosure" prose field). The entry, its
      // decision, and hasDisclosure are kept intact so recusal status
      // (isOfficiallyRecused / RecusedBadge, which read only decision and
      // hasDisclosure) remains visible on the Recusals page.
      for (const memberDisclosures of Object.values(draft.proposalDisclosure)) {
        for (const hiddenMemberId of hiddenRecommenderMemberIds) {
          const disclosure = memberDisclosures[hiddenMemberId];

          if (!disclosure) {
            continue;
          }

          disclosure.disclosure = "";
        }
      }
    }
  });
}

export function notesOrFeedbackVisible(
  {
    feedbackVisibleByDefault,
    evaluablePermissions,
  }: {
    feedbackVisibleByDefault: boolean;
    evaluablePermissions: {
      [evaluableId: string]: NotesAndFeedbackVisibility;
    };
  },
  evaluableId: string,
): boolean {
  const permissions = evaluablePermissions[evaluableId];
  return permissions === "visible"
    ? true
    : permissions === "hidden"
      ? false
      : feedbackVisibleByDefault;
}
